// Experimental WhatsApp view: every conversation from today as a small chat
// window on one screen, each with the customer's open issues on top. Built on the
// same endpoints as the regular inbox (/admin/whatsapp) — only the layout is new.
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import {
  getGrid,
  listMessages,
  sendMessage,
  markRead,
  mediaUrl,
  WaGridCard,
  WaGridIssue,
  WaMessage,
  WaSuggestion,
  suggestionFeedback,
  askGrokbot,
  createGridIssue,
  WaAccount,
} from './_requests'

const POLL_MS = 10000

const err = (e: any, fallback = 'שגיאה') => e?.response?.data?.error || e?.message || fallback

const accountName = (a?: WaAccount | null) =>
  !a ? '' : (a.company_name || [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email || a.username || '').trim()

const prettyPhone = (p?: string) => {
  const d = String(p || '').replace(/\D/g, '')
  const local = d.indexOf('972') === 0 ? '0' + d.slice(3) : d
  return local.length === 10 ? `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}` : local
}

const timeOf = (d?: string | null) => {
  if (!d) return ''
  const x = new Date(d)
  return isNaN(x.getTime()) ? '' : x.toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})
}

const statusMark = (m: WaMessage) => {
  if (m.direction !== 'out') return ''
  switch (m.status) {
    case 'read':
    case 'delivered':
      return '✓✓'
    case 'sent':
      return '✓'
    case 'failed':
      return '⚠'
    default:
      return '🕘'
  }
}

const URGENCY: Record<string, {bg: string; fg: string; label: string}> = {
  urgent: {bg: '#fde2e1', fg: '#b42318', label: 'דחוף'},
  high: {bg: '#fff1d6', fg: '#b54708', label: 'גבוה'},
  normal: {bg: '#e8eefc', fg: '#3045a8', label: ''},
  low: {bg: '#eef0f3', fg: '#5e6573', label: ''},
}

const IssueChip: FC<{issue: WaGridIssue}> = ({issue}) => {
  const u = URGENCY[issue.urgency] || URGENCY.normal
  const tip = [
    issue.title,
    issue.account_status === 'in_progress' ? 'בטיפול' : 'פתוח',
    issue.waiting === 'aryeh' ? 'ממתין לפיתוח' : issue.waiting === 'support' ? 'ממתין לתמיכה' : '',
    issue.due_date ? 'יעד: ' + issue.due_date : '',
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <Link
      to="/admin/issues"
      title={tip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        maxWidth: '100%',
        background: u.bg,
        color: u.fg,
        borderRadius: 10,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {issue.account_status === 'in_progress' ? '⏳' : '●'}
      {u.label && <span>{u.label}:</span>}
      <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{issue.title || 'תקלה'}</span>
    </Link>
  )
}

// Media in a mini chat. A wall of chats asks for dozens of files at once and the
// server answers a burst with 503 — so images load lazily (only when scrolled into
// view) and retry a few times with a growing delay; audio/video fetch on play.
const MiniMedia: FC<{m: WaMessage}> = ({m}) => {
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const base = mediaUrl(m._id)
  const src = attempt ? `${base}&r=${attempt}` : base

  if (m.type === 'image' || m.type === 'sticker') {
    if (failed) {
      return (
        <a href={base} target="_blank" rel="noreferrer" style={{display: 'block', fontSize: 11}}>
          🖼️ תמונה (לחץ לפתיחה)
        </a>
      )
    }
    return (
      <a href={base} target="_blank" rel="noreferrer">
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => {
            if (attempt >= 4) return setFailed(true)
            setTimeout(() => setAttempt((a) => a + 1), 1500 * (attempt + 1) + Math.random() * 1000)
          }}
          style={{maxWidth: 140, maxHeight: 140, minHeight: 40, borderRadius: 4, display: 'block', background: '#f0f2f5'}}
        />
      </a>
    )
  }
  if (m.type === 'audio' || m.type === 'voice' || m.type === 'ptt' || /^audio\//.test(m.media?.mime_type || '')) {
    return <audio src={base} controls preload="none" style={{width: 200, height: 32, display: 'block'}} />
  }
  if (m.type === 'video') {
    return <video src={base} controls preload="none" style={{maxWidth: 180, borderRadius: 4, display: 'block'}} />
  }
  return (
    <a href={base} target="_blank" rel="noreferrer" style={{display: 'block'}}>
      📎 {m.media?.filename || m.type}
    </a>
  )
}

// Ready-made issues. `route` is who it waits on by default: support, or Aryeh
// (tech). The description is prefilled and stays editable.
const ISSUE_TEMPLATES: {key: string; title: string; route: 'support' | 'aryeh'; urgency: 'normal' | 'high' | 'urgent'; text: string}[] = [
  {key: 'profile', title: 'פרופיל פייסבוק מנותק', route: 'support', urgency: 'high', text: 'הלקוח מדווח שהפרופיל מנותק / לא מפרסם. לבדוק את חיבור הפרופיל ולשלוח קישור התחברות.'},
  {key: 'not_posting', title: 'פוסטים לא עולים', route: 'aryeh', urgency: 'high', text: 'הלקוח מדווח שהפוסטים לא מתפרסמים בקבוצות. לבדוק תזמון, שגיאות פרסום ומצב הבוט של הפרופיל.'},
  {key: 'media', title: 'תמונות / וידאו לא עולים בפוסט', route: 'aryeh', urgency: 'normal', text: 'הפוסטים מתפרסמים בלי התמונות / הווידאו. לבדוק את המדיה בפוסט ואת לוג הפרסום.'},
  {key: 'marketplace', title: 'בעיה במרקטפלייס', route: 'aryeh', urgency: 'normal', text: 'בעיה במודעות המרקטפלייס (לא עולות / פרטים שגויים / מחיר). לבדוק את המודעות והסנכרון.'},
  {key: 'leads', title: 'לידים / תגובות לא מגיעים', route: 'aryeh', urgency: 'normal', text: 'הלקוח לא מקבל לידים / תגובות אוטומטיות לא נשלחות. לבדוק את המספר הווירטואלי ואת מנגנון התגובות.'},
  {key: 'groups', title: 'קבוצות / הצטרפות לקבוצות', route: 'support', urgency: 'normal', text: 'שאלה או בעיה בקבוצות של הלקוח (מעט קבוצות / הצטרפות / קבוצות לא רלוונטיות).'},
  {key: 'payment', title: 'תשלום / חיוב', route: 'support', urgency: 'normal', text: 'נושא תשלום: חיוב, חשבונית, כרטיס אשראי או קישור תשלום.'},
  {key: 'cancel', title: 'בקשת ביטול מנוי', route: 'support', urgency: 'urgent', text: 'הלקוח מבקש לבטל את המנוי. לחזור אליו, להבין את הסיבה ולנסות לשמר.'},
  {key: 'other', title: '', route: 'support', urgency: 'normal', text: ''},
]

const IssueForm: FC<{card: WaGridCard; messages: WaMessage[]; onDone: (created: boolean) => void}> = ({card, messages, onDone}) => {
  const [tpl, setTpl] = useState(ISSUE_TEMPLATES[0].key)
  const t = ISSUE_TEMPLATES.find((x) => x.key === tpl) || ISSUE_TEMPLATES[0]
  const customerLines = useMemo(() => {
    const lastIn = messages.filter((m) => m.direction === 'in' && m.body).slice(-3)
    const who = `לקוח: ${accountName(card.account) || card.contact?.name || ''} · ${prettyPhone(card.contact?.phone || card.account?.phone)}`
    return who + (lastIn.length ? '\nהודעות אחרונות מהלקוח:\n' + lastIn.map((m) => '> ' + m.body).join('\n') : '')
  }, [messages, card])
  const [title, setTitle] = useState(t.title)
  const [desc, setDesc] = useState(t.text + '\n\n' + customerLines)
  const [route, setRoute] = useState<'support' | 'aryeh'>(t.route)
  const [urgency, setUrgency] = useState<'low' | 'normal' | 'high' | 'urgent'>(t.urgency)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const pick = (key: string) => {
    const n = ISSUE_TEMPLATES.find((x) => x.key === key) || ISSUE_TEMPLATES[0]
    setTpl(key)
    setTitle(n.title)
    setDesc((n.text ? n.text + '\n\n' : '') + customerLines)
    setRoute(n.route)
    setUrgency(n.urgency)
  }

  const save = async () => {
    if (!card.account || !title.trim()) return setError('חסרה כותרת')
    setSaving(true)
    setError('')
    try {
      const {data} = await createGridIssue({account_id: card.account._id, title, description: desc, route, urgency})
      if (!data.ok) throw new Error(data.error || 'שגיאה')
      onDone(true)
    } catch (e) {
      setError(err(e))
      setSaving(false)
    }
  }

  return (
    <div style={{padding: 8, background: '#f8f9fb', borderBottom: '1px solid #e6e9f0', fontSize: 12}} onClick={(e) => e.stopPropagation()}>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6}}>
        {ISSUE_TEMPLATES.map((x) => (
          <button
            key={x.key}
            className={`btn btn-sm ${tpl === x.key ? 'btn-primary' : 'btn-light'}`}
            style={{padding: '1px 7px', fontSize: 11}}
            onClick={() => pick(x.key)}
          >
            {x.title || 'אחר'}
          </button>
        ))}
      </div>
      <input className="form-control form-control-sm mb-1" placeholder="כותרת" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="form-control form-control-sm mb-1" rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} style={{fontSize: 12}} />
      <div style={{display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap'}}>
        <div className="btn-group btn-group-sm">
          <button className={`btn ${route === 'support' ? 'btn-info' : 'btn-light'}`} onClick={() => setRoute('support')}>
            לתמיכה
          </button>
          <button className={`btn ${route === 'aryeh' ? 'btn-dark' : 'btn-light'}`} onClick={() => setRoute('aryeh')}>
            לצוות טכני (אריה)
          </button>
        </div>
        <select className="form-select form-select-sm" style={{width: 90}} value={urgency} onChange={(e) => setUrgency(e.target.value as any)}>
          <option value="low">נמוך</option>
          <option value="normal">רגיל</option>
          <option value="high">גבוה</option>
          <option value="urgent">דחוף</option>
        </select>
        <div style={{flex: 1}} />
        <button className="btn btn-sm btn-light" onClick={() => onDone(false)}>
          ביטול
        </button>
        <button className="btn btn-sm btn-warning" disabled={saving} onClick={save}>
          {saving ? 'פותח…' : 'פתח תקלה'}
        </button>
      </div>
      {error && <div style={{color: '#d92d20', marginTop: 4}}>{error}</div>}
    </div>
  )
}

const confColor = (n: number) => (n >= 80 ? '#067647' : n >= 50 ? '#b54708' : '#b42318')
const confBg = (n: number) => (n >= 80 ? '#dcfae6' : n >= 50 ? '#fef0c7' : '#fee4e2')

// Grok Bot's suggestions for the chat. Click = put it in the composer (nothing
// is sent until the admin presses send); × = dismiss, which is recorded.
const SuggestionStrip: FC<{items: WaSuggestion[]; picked: string; onPick: (s: WaSuggestion) => void; onDismiss: (s: WaSuggestion) => void}> = ({
  items,
  picked,
  onPick,
  onDismiss,
}) => (
  <div style={{padding: '6px 6px 0', display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 150, overflowY: 'auto'}}>
    {items.map((s) => (
      <div
        key={s.code}
        onClick={() => onPick(s)}
        title={(s.reasoning ? 'למה: ' + s.reasoning : '') + (s.intent ? '\nכוונה: ' + s.intent : '')}
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'flex-start',
          border: picked === s.code ? '1.5px solid #5b6ef5' : '1px solid #e3e6ee',
          background: '#fbfcff',
          borderRadius: 8,
          padding: '4px 6px',
          cursor: 'pointer',
          fontSize: 11.5,
        }}
      >
        <span
          style={{background: confBg(s.confidence), color: confColor(s.confidence), borderRadius: 6, padding: '0 5px', fontWeight: 700, flexShrink: 0}}
          title="כמה הבוט בטוח בתשובה (1-100)"
        >
          {s.confidence}
        </span>
        <span style={{flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', whiteSpace: 'pre-wrap'}}>
          {s.needs_human && '⚠️ '}
          {s.text}
        </span>
        <span style={{color: '#98a2b3', fontSize: 10, flexShrink: 0, direction: 'ltr'}}>{s.code}</span>
        <span
          onClick={(e) => {
            e.stopPropagation()
            onDismiss(s)
          }}
          title="לא רלוונטי"
          style={{color: '#98a2b3', flexShrink: 0, padding: '0 2px'}}
        >
          ×
        </span>
      </div>
    ))}
  </div>
)

// One small chat window. Reloads its thread only when the grid reports a newer
// last message, so a wall of 40 chats doesn't mean 40 requests every poll.
const MiniChat: FC<{card: WaGridCard; canSend: boolean; height: number; onChanged: () => void}> = ({
  card,
  canSend,
  height,
  onChanged,
}) => {
  const c = card.contact
  const [picked, setPicked] = useState<WaSuggestion | null>(null)
  const [hidden, setHidden] = useState<string[]>([])
  const [issueOpen, setIssueOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState('')
  // Nothing goes to a customer without a click on send AND a click on confirm.
  const [confirming, setConfirming] = useState(false)
  const suggestions = (card.suggestions || []).filter((s) => !hidden.includes(s.code))
  const [messages, setMessages] = useState<WaMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [unread, setUnread] = useState(c?.unread_count || 0)
  const scroller = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    if (!c) return
    setLoading(true)
    try {
      const {data} = await listMessages(c._id, {limit: 40})
      setMessages(data.messages || [])
    } catch (e) {
      setError(err(e))
    } finally {
      setLoading(false)
    }
  }, [c?._id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load()
  }, [load, c?.last_message_at])

  useEffect(() => {
    setUnread(c?.unread_count || 0)
  }, [c?.unread_count])

  useEffect(() => {
    const el = scroller.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  const clearUnread = () => {
    if (!c || !unread) return
    setUnread(0)
    markRead(c._id).catch(() => {})
  }

  const send = async () => {
    const text = draft.trim()
    if (!c || !text || sending) return
    setSending(true)
    setError('')
    try {
      const {data} = await sendMessage(c._id, text)
      if (data.message) setMessages((m) => [...m, data.message])
      if (!data.ok) setError(data.error || 'השליחה נכשלה')
      else {
        setDraft('')
        if (picked) {
          const same = text === picked.text.trim()
          suggestionFeedback(picked.code, {status: same ? 'used' : 'edited', final_text: text}).catch(() => {})
          setPicked(null)
          onChanged()
        }
      }
    } catch (e) {
      setError(err(e, 'השליחה נכשלה'))
    } finally {
      setSending(false)
    }
  }

  const dismiss = (s: WaSuggestion) => {
    setHidden((h) => [...h, s.code])
    if (picked?.code === s.code) setPicked(null)
    suggestionFeedback(s.code, {status: 'dismissed'}).catch(() => {})
  }

  const ask = async () => {
    if (!c || asking) return
    setAsking(true)
    setNote('')
    try {
      await askGrokbot(c._id)
      setNote('נשלח ל-Grok Bot — ההצעות יופיעו כאן בעוד כמה שניות')
    } catch (e) {
      setError(err(e))
    } finally {
      setAsking(false)
    }
  }

  const name = accountName(card.account) || c?.name || prettyPhone(c?.phone) || 'לקוח'
  const windowOpen = !!c?.window_open
  const composerOff = !c || !canSend || !windowOpen

  return (
    <div
      onClick={clearUnread}
      style={{
        background: '#fff',
        borderRadius: 12,
        border: unread ? '2px solid #25d366' : '1px solid #e6e9f0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      {/* Header */}
      <div style={{background: '#f0f2f5', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8}}>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
            {name}
          </div>
          <div style={{fontSize: 11, color: '#667781', direction: 'ltr', textAlign: 'right'}}>
            {prettyPhone(c?.phone || card.account?.phone)}
            {c?.name && card.account && c.name !== name ? ` · ${c.name}` : ''}
          </div>
        </div>
        {unread > 0 && (
          <span style={{background: '#25d366', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700}}>
            {unread}
          </span>
        )}
        {c && (
          <button className="btn btn-sm btn-light" style={{padding: '0 6px'}} title="בקש הצעות תשובה מ-Grok Bot" disabled={asking} onClick={ask}>
            🤖
          </button>
        )}
        {card.account && (
          <button
            className="btn btn-sm btn-light"
            style={{padding: '0 6px', fontSize: 11}}
            title="פתח תקלה ללקוח"
            onClick={(e) => {
              e.stopPropagation()
              setIssueOpen((v) => !v)
            }}
          >
            + תקלה
          </button>
        )}
        {c && (
          <Link to={`/admin/whatsapp?c=${c._id}`} title="פתח בתיבה המלאה" style={{fontSize: 14, color: '#54656f'}}>
            <i className="bi bi-box-arrow-up-left"></i>
          </Link>
        )}
      </div>

      {issueOpen && (
        <IssueForm
          card={card}
          messages={messages}
          onDone={(created) => {
            setIssueOpen(false)
            if (created) onChanged()
          }}
        />
      )}

      {/* Open issues */}
      {card.issues.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '6px 8px',
            background: '#fffaf0',
            borderBottom: '1px solid #f1e6cf',
            maxHeight: 54,
            overflowY: 'auto',
          }}
        >
          {[...card.issues].sort((a, b) => (URGENCY_RANK[b.urgency] || 0) - (URGENCY_RANK[a.urgency] || 0)).map((i) => (
            <IssueChip key={i._id} issue={i} />
          ))}
        </div>
      )}

      {/* Thread */}
      <div ref={scroller} style={{height, overflowY: 'auto', background: '#efeae2', padding: '8px 8px'}}>
        {!c && <div style={{color: '#667781', fontSize: 12, textAlign: 'center', marginTop: 20}}>אין שיחת וואטסאפ עם הלקוח</div>}
        {c && loading && !messages.length && <div style={{color: '#667781', fontSize: 12}}>טוען…</div>}
        {messages.map((m) => (
          <div
            key={m._id}
            style={{display: 'flex', justifyContent: m.direction === 'in' ? 'flex-start' : 'flex-end', marginBottom: 4}}
          >
            <div
              style={{
                maxWidth: '85%',
                background: m.direction === 'in' ? '#fff' : '#d9fdd3',
                borderRadius: 8,
                padding: '4px 7px',
                fontSize: 12,
                lineHeight: 1.35,
                boxShadow: '0 1px 1px rgba(0,0,0,.08)',
                border: m.status === 'failed' ? '1px solid #f1aeb5' : 'none',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {m.media?.url && <MiniMedia m={m} />}
              {m.type === 'contacts' ? '👤 איש קשר' : m.body || m.media?.caption || ''}
              <span style={{fontSize: 10, color: '#8696a0', marginInlineStart: 6, whiteSpace: 'nowrap'}}>
                {timeOf(m.sent_at)} {statusMark(m)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {suggestions.length > 0 && (
        <SuggestionStrip
          items={suggestions}
          picked={picked?.code || ''}
          onPick={(s) => {
            setPicked(s)
            setConfirming(false)
            setDraft(s.text)
          }}
          onDismiss={dismiss}
        />
      )}
      {note && <div style={{color: '#3045a8', fontSize: 11, padding: '4px 8px 0'}}>{note}</div>}

      {/* Composer */}
      <div style={{display: 'flex', gap: 6, padding: 6, borderTop: suggestions.length ? 'none' : '1px solid #e6e9f0', alignItems: 'flex-end'}}>
        <textarea
          className="form-control form-control-sm"
          rows={draft.length > 60 || draft.includes('\n') ? 3 : 1}
          style={{resize: 'none', fontSize: 12}}
          value={draft}
          disabled={composerOff || sending}
          onFocus={clearUnread}
          onChange={(e) => {
            setDraft(e.target.value)
            setConfirming(false)
            if (!e.target.value) setPicked(null)
          }}
          placeholder={!c ? '—' : !windowOpen ? 'חלון 24 שעות סגור' : 'הודעה…'}
        />
        <button
          className="btn btn-sm btn-success"
          disabled={composerOff || sending || !draft.trim()}
          onClick={() => setConfirming(true)}
          title="שליחה (תתבקש לאשר)"
        >
          <i className="bi bi-send"></i>
        </button>
      </div>
      {confirming && draft.trim() && (
        <div style={{margin: '0 6px 6px', padding: 8, border: '1.5px solid #f79009', borderRadius: 8, background: '#fffaeb', fontSize: 12}}>
          <div style={{fontWeight: 700, marginBottom: 4}}>לשלוח ללקוח את ההודעה הזו?</div>
          <div style={{whiteSpace: 'pre-wrap', background: '#fff', borderRadius: 6, padding: 6, maxHeight: 120, overflowY: 'auto', marginBottom: 6}}>
            {draft.trim()}
          </div>
          <div style={{display: 'flex', gap: 6, justifyContent: 'flex-end'}}>
            <button className="btn btn-sm btn-light" onClick={() => setConfirming(false)}>
              ביטול
            </button>
            <button
              className="btn btn-sm btn-success"
              disabled={sending}
              onClick={async () => {
                await send()
                setConfirming(false)
              }}
            >
              {sending ? 'שולח…' : 'כן, לשלוח'}
            </button>
          </div>
        </div>
      )}
      {error && <div style={{color: '#d92d20', fontSize: 11, padding: '0 8px 6px'}}>{error}</div>}
    </div>
  )
}

// Sort key: the most urgent open issue on the customer (urgent > high > normal
// > low > none). Ties fall back to unread, then most recent message.
const URGENCY_RANK: Record<string, number> = {urgent: 4, high: 3, normal: 2, low: 1}
const urgencyRank = (cd: WaGridCard) => cd.issues.reduce((mx, i) => Math.max(mx, URGENCY_RANK[i.urgency] || 0), 0)

const SIZES = {
  s: {min: 260, height: 200},
  m: {min: 320, height: 280},
  l: {min: 400, height: 380},
}

const WhatsappGridPage: FC = () => {
  const [cards, setCards] = useState<WaGridCard[]>([])
  const [canSend, setCanSend] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [includeIssues, setIncludeIssues] = useState(false)
  const [onlyUnread, setOnlyUnread] = useState(false)
  const [search, setSearch] = useState('')
  const [size, setSize] = useState<keyof typeof SIZES>('m')
  const busy = useRef(false)

  const load = useCallback(async () => {
    if (busy.current) return
    busy.current = true
    try {
      const {data} = await getGrid(includeIssues)
      setCards(data.cards || [])
      setCanSend(!!data.can_send)
      setError('')
    } catch (e) {
      setError(err(e))
    } finally {
      busy.current = false
      setLoading(false)
    }
  }, [includeIssues])

  useEffect(() => {
    setLoading(true)
    load()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load])

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    const digits = q.replace(/\D/g, '')
    return cards
      .filter((cd) => {
        if (onlyUnread && !(cd.contact?.unread_count || 0)) return false
        if (!q) return true
        const hay = [accountName(cd.account), cd.contact?.name, cd.account?.email].join(' ').toLowerCase()
        const phones = [cd.contact?.phone, cd.account?.phone].join(' ').replace(/\D/g, '')
        return hay.includes(q) || (!!digits && (phones.includes(digits) || phones.includes(digits.replace(/^0/, '972'))))
      })
      .sort(
        (a, b) =>
          urgencyRank(b) - urgencyRank(a) ||
          Number((b.contact?.unread_count || 0) > 0) - Number((a.contact?.unread_count || 0) > 0) ||
          String(b.contact?.last_message_at || '').localeCompare(String(a.contact?.last_message_at || ''))
      )
  }, [cards, search, onlyUnread])

  const unreadTotal = cards.filter((c) => (c.contact?.unread_count || 0) > 0).length
  const withIssues = cards.filter((c) => c.issues.length > 0).length
  const sz = SIZES[size]

  return (
    <div style={{padding: 16}}>
      <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 14}}>
        <h3 style={{margin: 0}}>
          <i className="bi bi-grid-3x3-gap" style={{marginInlineEnd: 6}}></i>
          כל השיחות של היום
        </h3>
        <span style={{color: '#667781', fontSize: 13}}>
          {cards.length} שיחות · {unreadTotal} לא נקראו · {withIssues} עם תקלות פתוחות
        </span>
        <div style={{flex: 1}} />
        <input
          className="form-control form-control-sm"
          style={{width: 200}}
          placeholder="חיפוש שם / טלפון"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, margin: 0}}>
          <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} />
          רק לא נקראו
        </label>
        <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, margin: 0}}>
          <input type="checkbox" checked={includeIssues} onChange={(e) => setIncludeIssues(e.target.checked)} />
          הוסף לקוחות עם תקלות פתוחות
        </label>
        <div className="btn-group btn-group-sm">
          {(['s', 'm', 'l'] as const).map((k) => (
            <button key={k} className={`btn ${size === k ? 'btn-primary' : 'btn-light'}`} onClick={() => setSize(k)}>
              {k === 's' ? 'קטן' : k === 'm' ? 'בינוני' : 'גדול'}
            </button>
          ))}
        </div>
      </div>

      {!canSend && (
        <div className="alert alert-warning py-2">שליחת וואטסאפ לא מוגדרת בשרת — אפשר לקרוא אבל לא לשלוח.</div>
      )}
      {error && <div className="alert alert-danger py-2">{error}</div>}
      {loading && !cards.length && <div style={{color: '#667781'}}>טוען…</div>}
      {!loading && !shown.length && <div style={{color: '#667781'}}>אין שיחות להצגה</div>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${sz.min}px, 1fr))`,
          gap: 12,
        }}
      >
        {shown.map((cd) => (
          <MiniChat
            key={cd.contact?._id || 'acc-' + cd.account?._id}
            card={cd}
            canSend={canSend}
            height={sz.height}
            onChanged={load}
          />
        ))}
      </div>
    </div>
  )
}

export default WhatsappGridPage
